import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { LogOut, Mail, ShoppingBag, Users, Phone, Calendar, RefreshCw, Inbox } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY_STORAGE = "aradela_admin_key";

const fmtDate = (d) => {
  try {
    return new Date(d).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return d;
  }
};

const Login = ({ onSuccess }) => {
  const [pwd, setPwd] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password: pwd });
      sessionStorage.setItem(KEY_STORAGE, res.data.key);
      onSuccess(res.data.key);
    } catch {
      toast.error("Invalid password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6" data-testid="admin-login">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#141414] border border-white/10 p-10"
      >
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 border border-[#D4A017] flex items-center justify-center">
            <span className="font-display text-[#D4A017] text-xl leading-none">A</span>
          </div>
          <div>
            <div className="font-display text-white text-xl tracking-wider">ARADELÁ</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4A017]">Admin</div>
          </div>
        </div>
        <h2 className="font-display text-white text-2xl mb-2">Welcome back</h2>
        <p className="text-white/55 text-sm mb-8">Enter the admin password to continue.</p>
        <input
          type="password"
          required
          value={pwd}
          onChange={(e) => setPwd(e.target.value)}
          placeholder="Password"
          className="w-full bg-transparent border-b border-white/15 py-3 text-white focus:outline-none focus:border-[#D4A017]"
          data-testid="admin-password-input"
          autoFocus
        />
        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full justify-center mt-8 disabled:opacity-60"
          data-testid="admin-login-submit"
        >
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-[#141414] border border-white/5 p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <span className="text-white/50 uppercase text-xs tracking-[0.3em]">{label}</span>
      <Icon size={18} className="text-[#D4A017]" />
    </div>
    <div className="font-display text-white text-4xl">{value}</div>
  </div>
);

const TabBtn = ({ active, onClick, children, testid }) => (
  <button
    onClick={onClick}
    className={`px-5 py-2.5 text-xs uppercase tracking-[0.2em] border transition-colors ${
      active
        ? "bg-[#D4A017] text-black border-[#D4A017]"
        : "border-white/15 text-white/70 hover:border-[#D4A017] hover:text-[#D4A017]"
    }`}
    data-testid={testid}
  >
    {children}
  </button>
);

export default function Admin() {
  const [authed, setAuthed] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [tab, setTab] = useState("orders");
  const [stats, setStats] = useState({ contacts: 0, orders: 0, newsletter: 0, reservations: 0 });
  const [contacts, setContacts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [newsletter, setNewsletter] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleAuth = (key) => {
    setApiKey(key);
    setAuthed(true);
  };

  const logout = () => {
    sessionStorage.removeItem(KEY_STORAGE);
    setAuthed(false);
    setApiKey(null);
  };

  const headers = useCallback(() => ({ "X-Admin-Key": apiKey }), [apiKey]);

  const loadAll = useCallback(async () => {
    if (!apiKey) return;
    setLoading(true);
    try {
      const [s, c, o, n, r] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: headers() }),
        axios.get(`${API}/admin/contacts`, { headers: headers() }),
        axios.get(`${API}/admin/orders`, { headers: headers() }),
        axios.get(`${API}/admin/newsletter`, { headers: headers() }),
        axios.get(`${API}/admin/reservations`, { headers: headers() }),
      ]);
      setStats(s.data);
      setContacts(c.data);
      setOrders(o.data);
      setNewsletter(n.data);
      setReservations(r.data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  }, [apiKey, headers]);

  useEffect(() => {
    const stored = sessionStorage.getItem(KEY_STORAGE);
    if (stored) handleAuth(stored);
  }, []);

  useEffect(() => {
    if (authed) loadAll();
  }, [authed, loadAll]);

  if (!authed) return <Login onSuccess={handleAuth} />;

  return (
    <main className="min-h-screen bg-[#0A0A0A] pt-28 pb-20" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="text-[#D4A017] uppercase tracking-[0.3em] text-xs">Admin</span>
            <h1 className="font-display text-white text-4xl md:text-5xl mt-3 leading-tight">
              Dashboard
            </h1>
            <p className="text-white/55 text-sm mt-2">Manage orders, messages and subscribers.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={loadAll}
              className="btn-secondary"
              data-testid="admin-refresh"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={logout} className="btn-ghost" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-10">
          <StatCard icon={Calendar} label="Reservations" value={stats.reservations || 0} testid="stat-reservations" />
          <StatCard icon={ShoppingBag} label="Orders" value={stats.orders} testid="stat-orders" />
          <StatCard icon={Mail} label="Messages" value={stats.contacts} testid="stat-contacts" />
          <StatCard icon={Users} label="Subscribers" value={stats.newsletter} testid="stat-newsletter" />
        </div>

        <div className="flex gap-2 mb-8 overflow-x-auto no-scrollbar" data-testid="admin-tabs">
          <TabBtn active={tab === "reservations"} onClick={() => setTab("reservations")} testid="tab-reservations">
            Reservations ({reservations.length})
          </TabBtn>
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="tab-orders">
            Orders ({orders.length})
          </TabBtn>
          <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")} testid="tab-contacts">
            Messages ({contacts.length})
          </TabBtn>
          <TabBtn active={tab === "newsletter"} onClick={() => setTab("newsletter")} testid="tab-newsletter">
            Newsletter ({newsletter.length})
          </TabBtn>
        </div>

        {tab === "reservations" && (
          <div className="space-y-4" data-testid="reservations-panel">
            {reservations.length === 0 && (
              <div className="bg-[#141414] border border-white/5 p-10 text-center text-white/50">
                <Inbox size={28} className="mx-auto mb-3 text-white/30" />
                No reservations yet.
              </div>
            )}
            {reservations.map((r) => (
              <div key={r.id} className="bg-[#141414] border border-white/5 p-6" data-testid={`reservation-${r.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-4 mb-3 pb-3 border-b border-white/5">
                  <div>
                    <div className="font-display text-white text-xl">{r.name}</div>
                    <div className="flex flex-wrap items-center gap-4 text-white/55 text-sm mt-1">
                      <span className="flex items-center gap-1.5"><Phone size={12} /> {r.phone}</span>
                      {r.email && <a href={`mailto:${r.email}`} className="hover:text-[#D4A017]">{r.email}</a>}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-impact uppercase text-[#D4A017] text-xl tracking-wider">
                      {r.party_size} {r.party_size === 1 ? "guest" : "guests"}
                    </div>
                    <div className="text-white/70 text-sm mt-1">
                      {r.date} · {r.time}
                    </div>
                  </div>
                </div>
                <div className="flex justify-between items-start gap-3 text-xs">
                  <div className="text-white/40">Booked {fmtDate(r.created_at)}</div>
                  <div className="text-white/40 uppercase tracking-[0.2em]">{r.status}</div>
                </div>
                {r.notes && (
                  <p className="text-white/55 text-sm mt-3 italic border-l-2 border-[#D4A017] pl-3">
                    Note: {r.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="space-y-4" data-testid="orders-panel">
            {orders.length === 0 && (
              <div className="bg-[#141414] border border-white/5 p-10 text-center text-white/50">
                <Inbox size={28} className="mx-auto mb-3 text-white/30" />
                No orders yet.
              </div>
            )}
            {orders.map((o) => (
              <div key={o.id} className="bg-[#141414] border border-white/5 p-6" data-testid={`order-${o.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-4 mb-4 pb-4 border-b border-white/5">
                  <div>
                    <div className="font-display text-white text-xl">{o.customer_name}</div>
                    <div className="flex items-center gap-4 text-white/55 text-sm mt-1">
                      <span className="flex items-center gap-1.5"><Phone size={12} /> {o.phone}</span>
                      <span className="flex items-center gap-1.5"><Calendar size={12} /> {fmtDate(o.created_at)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-[#D4A017] text-2xl">£{o.total.toFixed(2)}</div>
                    <div className="text-white/40 text-xs uppercase tracking-[0.2em] mt-1">{o.status}</div>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-white/75">
                  {o.items.map((i, idx) => (
                    <li key={idx} className="flex justify-between">
                      <span>{i.quantity} × {i.name}</span>
                      <span className="text-white/50">£{(i.price * i.quantity).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
                {o.notes && (
                  <p className="text-white/55 text-sm mt-4 italic border-l-2 border-[#D4A017] pl-3">
                    Note: {o.notes}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "contacts" && (
          <div className="space-y-4" data-testid="contacts-panel">
            {contacts.length === 0 && (
              <div className="bg-[#141414] border border-white/5 p-10 text-center text-white/50">
                <Inbox size={28} className="mx-auto mb-3 text-white/30" />
                No messages yet.
              </div>
            )}
            {contacts.map((c) => (
              <div key={c.id} className="bg-[#141414] border border-white/5 p-6" data-testid={`contact-${c.id}`}>
                <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
                  <div>
                    <div className="font-display text-white text-xl">{c.name}</div>
                    <div className="flex flex-wrap items-center gap-4 text-white/55 text-sm mt-1">
                      <a href={`mailto:${c.email}`} className="hover:text-[#D4A017]">{c.email}</a>
                      {c.phone && <span className="flex items-center gap-1.5"><Phone size={12} /> {c.phone}</span>}
                    </div>
                  </div>
                  <span className="text-white/40 text-xs">{fmtDate(c.created_at)}</span>
                </div>
                {c.subject && (
                  <div className="text-[#D4A017] text-sm uppercase tracking-[0.2em] mb-2">{c.subject}</div>
                )}
                <p className="text-white/75 leading-relaxed whitespace-pre-wrap">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {tab === "newsletter" && (
          <div className="bg-[#141414] border border-white/5" data-testid="newsletter-panel">
            {newsletter.length === 0 ? (
              <div className="p-10 text-center text-white/50">
                <Inbox size={28} className="mx-auto mb-3 text-white/30" />
                No subscribers yet.
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.3em] text-white/40 border-b border-white/5">
                    <th className="px-6 py-4">Email</th>
                    <th className="px-6 py-4">Subscribed</th>
                  </tr>
                </thead>
                <tbody>
                  {newsletter.map((s) => (
                    <tr key={s.id} className="border-b border-white/5 last:border-b-0" data-testid={`newsletter-${s.id}`}>
                      <td className="px-6 py-4 text-white">{s.email}</td>
                      <td className="px-6 py-4 text-white/55 text-sm">{fmtDate(s.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
